/*!
 * Girlbasket Theme v1.0.0
 * Copyright (c) 2026 Om. All rights reserved.
 *
 * This file is part of the Girlbasket theme, licensed only for use on the
 * Girlbasket store (girlbasket.myshopify.com / girlbasket.com). Copying,
 * modifying, redistributing or reselling this file, in whole or in part,
 * without written permission from Om is prohibited. See LICENSE.md.
 *
 * File: assets/gb-core.js
 */
(() => {
  'use strict';

  const GB = (window.GB = window.GB || {});

  /* ---------------------------------------------------------------------
     Licence check (second layer; the first runs in the layout Liquid)
     --------------------------------------------------------------------- */
  const licensedStores = [atob('Z2lybGJhc2tldC5teXNob3BpZnkuY29t')];
  const currentStore = window.Shopify && window.Shopify.shop;
  if (currentStore && !licensedStores.includes(currentStore)) {
    document.body.innerHTML =
      '<div role="alert" style="min-height:100vh;display:flex;align-items:center;justify-content:center;' +
      'padding:24px;background:#232323;color:#fff;font:15px/1.6 system-ui,sans-serif;text-align:center">' +
      '<div><h1 style="font-size:26px;margin:0 0 12px">Unlicensed theme</h1>' +
      '<p>This theme is licensed only for the Girlbasket store.</p>' +
      '<p>&copy; 2026 Om &middot; Girlbasket Theme</p></div></div>';
    return;
  }

  /* ---------------------------------------------------------------------
     Event bus: GB.events.on('cart:updated', fn) / GB.events.emit(...)
     Events fire on document as "gb:<name>" CustomEvents.
     --------------------------------------------------------------------- */
  GB.events = {
    on(name, handler) {
      const listener = (event) => handler(event.detail, event);
      document.addEventListener(`gb:${name}`, listener);
      return () => document.removeEventListener(`gb:${name}`, listener);
    },
    emit(name, detail = {}) {
      document.dispatchEvent(new CustomEvent(`gb:${name}`, { detail }));
    }
  };

  GB.debounce = (fn, wait = 200) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  /* ---------------------------------------------------------------------
     Breakpoints and viewport
     --------------------------------------------------------------------- */
  GB.media = {
    tablet: window.matchMedia('(min-width: 768px)'),
    desktop: window.matchMedia('(min-width: 1024px)'),
    hover: window.matchMedia('(hover: hover) and (pointer: fine)'),
    reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)')
  };
  GB.isDesktop = () => GB.media.desktop.matches;
  GB.media.desktop.addEventListener('change', (event) => {
    GB.events.emit('breakpoint:change', { desktop: event.matches });
  });

  let lastWidth = window.innerWidth;
  window.addEventListener(
    'resize',
    GB.debounce(() => {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      GB.events.emit('viewport:resize', { width: lastWidth });
    }, 200)
  );

  /* ---------------------------------------------------------------------
     Focus helpers
     --------------------------------------------------------------------- */
  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    'iframe',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  GB.focusables = (root) =>
    [...root.querySelectorAll(FOCUSABLE)].filter(
      (el) => el.offsetWidth || el.offsetHeight || el.getClientRects().length
    );

  // Keeps Tab focus inside container. Returns a function that releases it.
  GB.trapFocus = (container, initial) => {
    const onKeydown = (event) => {
      if (event.key !== 'Tab') return;
      const items = GB.focusables(container);
      if (!items.length) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeydown);
    const target = initial || GB.focusables(container)[0] || container;
    target.focus({ preventScroll: true });
    return () => document.removeEventListener('keydown', onKeydown);
  };

  let scrollLocks = 0;
  GB.lockScroll = () => {
    scrollLocks += 1;
    document.documentElement.classList.add('gb-scroll-locked');
  };
  GB.unlockScroll = () => {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (!scrollLocks) document.documentElement.classList.remove('gb-scroll-locked');
  };

  /* ---------------------------------------------------------------------
     Section Rendering API
     --------------------------------------------------------------------- */
  GB.fetchSections = async (url, sectionIds) => {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('sections', [].concat(sectionIds).join(','));
    const response = await fetch(target, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Section request failed (${response.status})`);
    return response.json();
  };

  GB.parseHtml = (html) => new DOMParser().parseFromString(html, 'text/html');

  // Scroll position of a child inside a horizontal scroll container.
  GB.offsetIn = (child, track) =>
    child.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;

  /* ---------------------------------------------------------------------
     Money
     --------------------------------------------------------------------- */
  GB.formatMoney = (cents, format = GB.moneyFormat || '{{amount}}') => {
    const value = Number(cents) || 0;
    const group = (precision, thousands = ',', decimal = '.') => {
      const [whole, fraction] = (value / 100).toFixed(precision).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? grouped + decimal + fraction : grouped;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return group(0);
        case 'amount_with_comma_separator':
          return group(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return group(2, "'", '.');
        case 'amount_no_decimals_with_space_separator':
          return group(0, ' ', '.');
        case 'amount_with_space_separator':
          return group(2, ' ', ',');
        case 'amount_with_period_and_space_separator':
          return group(2, ' ', '.');
        default:
          return group(2);
      }
    });
  };

  /* ---------------------------------------------------------------------
     <gb-drawer>: side drawers and modals.
     Open with any button that has data-gb-open="<drawer id>".
     Close with [data-gb-close] inside it, Esc, or the overlay.
     --------------------------------------------------------------------- */
  class GbDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-gb-close]')) this.close();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
    }

    get isOpen() {
      return this.hasAttribute('open');
    }

    open(opener) {
      if (this.isOpen) return;
      this.opener = opener || document.activeElement;
      this.setAttribute('open', '');
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'true');
      GB.lockScroll();
      const panel = this.querySelector('[data-gb-panel]') || this;
      const initial = this.querySelector('[data-gb-autofocus]');
      requestAnimationFrame(() => {
        this.releaseFocus = GB.trapFocus(panel, initial);
      });
      GB.events.emit('drawer:open', { id: this.id, drawer: this });
    }

    close() {
      if (!this.isOpen) return;
      this.removeAttribute('open');
      if (this.releaseFocus) this.releaseFocus();
      GB.unlockScroll();
      if (this.opener && this.opener.setAttribute) {
        this.opener.setAttribute('aria-expanded', 'false');
        if (this.opener.focus) this.opener.focus({ preventScroll: true });
      }
      GB.events.emit('drawer:close', { id: this.id, drawer: this });
    }
  }
  if (!customElements.get('gb-drawer')) customElements.define('gb-drawer', GbDrawer);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-gb-open]');
    if (!opener) return;
    const target = document.getElementById(opener.dataset.gbOpen);
    if (target && typeof target.open === 'function') {
      event.preventDefault();
      target.open(opener);
    }
  });

  /* ---------------------------------------------------------------------
     <gb-countdown data-end="ISO date">
     --------------------------------------------------------------------- */
  class GbCountdown extends HTMLElement {
    connectedCallback() {
      this.end = Date.parse(this.dataset.end);
      if (Number.isNaN(this.end)) return;
      this.nums = {};
      this.querySelectorAll('[data-unit]').forEach((el) => {
        this.nums[el.dataset.unit] = el;
      });
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const left = Math.max(0, this.end - Date.now());
      const total = Math.floor(left / 1000);
      const values = {
        days: Math.floor(total / 86400),
        hours: Math.floor((total % 86400) / 3600),
        minutes: Math.floor((total % 3600) / 60),
        seconds: total % 60
      };
      Object.entries(this.nums).forEach(([unit, el]) => {
        el.textContent = String(values[unit]).padStart(2, '0');
      });
      if (left === 0) this.finish();
    }

    finish() {
      clearInterval(this.timer);
      if (this.hasAttribute('data-hide-on-end')) {
        const host = this.closest('[data-gb-countdown-host]') || this;
        host.hidden = true;
        GB.events.emit('countdown:ended', { host });
        return;
      }
      if (this.dataset.endText) {
        this.textContent = this.dataset.endText;
        this.classList.add('is-ended');
      }
    }
  }
  if (!customElements.get('gb-countdown')) customElements.define('gb-countdown', GbCountdown);

  /* ---------------------------------------------------------------------
     Selects that submit their form on change (sorting, localization)
     --------------------------------------------------------------------- */
  document.addEventListener('change', (event) => {
    const field = event.target.closest('[data-gb-autosubmit]');
    if (!field || !field.form) return;
    if (field.form.requestSubmit) field.form.requestSubmit();
    else field.form.submit();
  });

  /* ---------------------------------------------------------------------
     Cart count in the header
     --------------------------------------------------------------------- */
  GB.events.on('cart:updated', ({ cart }) => {
    if (!cart) return;
    document.querySelectorAll('[data-gb-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
  });

  /* ---------------------------------------------------------------------
     <gb-qty>: quantity field with − and + buttons
     --------------------------------------------------------------------- */
  class GbQty extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-gb-qty]');
        if (!button || !this.input) return;
        const step = Number(this.input.step) || 1;
        const min = this.input.min !== '' ? Number(this.input.min) : 0;
        const max = this.input.max !== '' ? Number(this.input.max) : Infinity;
        const current = Number(this.input.value) || 0;
        const next = button.dataset.gbQty === 'plus' ? current + step : current - step;
        const clamped = Math.min(max, Math.max(min, next));
        if (clamped === current) return;
        this.input.value = clamped;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  if (!customElements.get('gb-qty')) customElements.define('gb-qty', GbQty);

  /* ---------------------------------------------------------------------
     <gb-carousel>: scroll-snap track with arrows and optional dots
     --------------------------------------------------------------------- */
  class GbCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gb-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-gb-carousel-prev]');
      this.next = this.querySelector('[data-gb-carousel-next]');
      this.dots = [...this.querySelectorAll('[data-gb-carousel-dot]')];

      if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
      if (this.next) this.next.addEventListener('click', () => this.step(1));
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index)));
      this.onScroll = GB.debounce(() => this.update(), 60);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);

      this.autoplay = Number(this.dataset.autoplay) || 0;
      if (this.autoplay && !GB.media.reducedMotion.matches) {
        this.start();
        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.start());
        this.addEventListener('focusin', () => this.stop());
      }
      this.update();
    }

    disconnectedCallback() {
      this.stop();
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    get slides() {
      return [...this.track.children];
    }

    step(direction) {
      const slide = this.slides[0];
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const amount = slide ? slide.offsetWidth + gap : this.track.clientWidth;
      const perView = Math.max(1, Math.floor((this.track.clientWidth + gap) / amount));
      const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
      this.track.scrollBy({ left: direction * amount * perView * rtl, behavior: 'smooth' });
    }

    goTo(index) {
      const slide = this.slides[index];
      if (!slide) return;
      this.track.scrollTo({ left: GB.offsetIn(slide, this.track), behavior: 'smooth' });
    }

    current() {
      const left = this.track.scrollLeft;
      let index = 0;
      let best = Infinity;
      this.slides.forEach((slide, i) => {
        const distance = Math.abs(GB.offsetIn(slide, this.track) - left);
        if (distance < best) {
          best = distance;
          index = i;
        }
      });
      return index;
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      const scrollable = max > 0;
      this.classList.toggle('is-scrollable', scrollable);
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = Math.abs(this.track.scrollLeft) >= max;
      if (this.dots.length) {
        const index = this.current();
        this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
      }
    }

    start() {
      this.stop();
      this.timer = setInterval(() => {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        if (this.track.scrollLeft >= max) this.goTo(0);
        else this.step(1);
      }, this.autoplay * 1000);
    }

    stop() {
      clearInterval(this.timer);
    }
  }
  if (!customElements.get('gb-carousel')) customElements.define('gb-carousel', GbCarousel);

  /* ---------------------------------------------------------------------
     <gb-deferred-media>: loads a video iframe only after a click
     --------------------------------------------------------------------- */
  class GbDeferredMedia extends HTMLElement {
    connectedCallback() {
      this.querySelector('[data-gb-deferred-play]')?.addEventListener('click', () => this.load());
    }

    load() {
      if (this.loaded) return;
      const template = this.querySelector('template');
      if (!template) return;
      this.loaded = true;
      this.appendChild(template.content.cloneNode(true));
      this.classList.add('is-loaded');
      const media = this.querySelector('iframe, video');
      if (media && media.play) media.play();
    }
  }
  if (!customElements.get('gb-deferred-media')) customElements.define('gb-deferred-media', GbDeferredMedia);

  /* ---------------------------------------------------------------------
     <gb-share>: native share sheet when available, copy link otherwise
     --------------------------------------------------------------------- */
  class GbShare extends HTMLElement {
    connectedCallback() {
      const copy = this.querySelector('[data-gb-copy-link]');
      const status = this.querySelector('[data-gb-share-status]');
      if (!copy) return;
      copy.addEventListener('click', async () => {
        const url = this.dataset.url || window.location.href;
        try {
          if (navigator.share && GB.media.hover.matches === false) {
            await navigator.share({ title: this.dataset.title, url });
            return;
          }
          await navigator.clipboard.writeText(url);
          if (status) {
            status.textContent = GB.strings.copied;
            setTimeout(() => {
              status.textContent = '';
            }, 2500);
          }
        } catch (error) {
          /* Share cancelled or clipboard blocked. */
        }
      });
    }
  }
  if (!customElements.get('gb-share')) customElements.define('gb-share', GbShare);

  /* ---------------------------------------------------------------------
     Open links to other websites in a new tab
     --------------------------------------------------------------------- */
  if (GB.settings && GB.settings.externalLinksNewTab) {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href^="http"]');
      if (!link || link.target || link.host === window.location.host) return;
      if (link.closest('[data-no-new-tab]')) return;
      link.target = '_blank';
      link.rel = 'noopener';
    });
  }

  /* ---------------------------------------------------------------------
     Toast message (used for cart notices and errors)
     --------------------------------------------------------------------- */
  GB.toast = (message, { type = 'info', timeout = 4000 } = {}) => {
    let region = document.getElementById('GbToasts');
    if (!region) {
      region = document.createElement('div');
      region.id = 'GbToasts';
      region.className = 'gb-toasts';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      document.body.appendChild(region);
    }
    const toast = document.createElement('div');
    toast.className = `gb-toast gb-toast--${type}`;
    toast.textContent = message;
    region.appendChild(toast);
    setTimeout(() => toast.remove(), timeout);
  };

  /* ---------------------------------------------------------------------
     Reveal on scroll (skips the first section and reduced-motion users)
     --------------------------------------------------------------------- */
  const designMode = window.Shopify && window.Shopify.designMode;
  if (
    GB.settings &&
    GB.settings.animations &&
    !designMode &&
    !GB.media.reducedMotion.matches &&
    'IntersectionObserver' in window
  ) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -40px 0px' }
    );
    GB.observeReveal = (root = document) => {
      root.querySelectorAll('.gb-reveal:not(.is-revealed)').forEach((el) => observer.observe(el));
    };
    document.documentElement.classList.add('gb-reveal-ready');
    GB.observeReveal();
  } else {
    GB.observeReveal = () => {};
  }
})();
